import { Avatar, Box, Divider, Drawer, Grid, Stack, Tooltip, Typography } from '@mui/material'
import InstallationDetailContainer from './InstallationDetailContainer'
import InstallationEditAction from './InstallationEditAction'
import { useEffect, useState } from 'react'
import { getInstallation } from 'services/kraken-core/installations/installations.service'
import { InstallationDetail } from 'common/types/kraken-core/Installation'
import { getIntegrationModule } from 'services/kraken-core/integration_module/integration.service'
import { IntegrationModule } from 'common/types/kraken-core/IntegrationModule'
import { toast } from 'react-toastify'
import Loading from 'atoms/Loading'
import mapping from 'atoms/Icon/mapping'
import { IconButton, Text } from '@loadsmart/loadsmart-ui'
import { OpenInNew } from '@mui/icons-material'
import { ROUTES } from 'routing/routes'

export interface InstallationQuickViewProps {
  installationId?: string
  onClose?: () => void
  open?: boolean
}

function InstallationQuickView({ installationId, open, onClose }: InstallationQuickViewProps) {
  const [installation, setInstallation] = useState<InstallationDetail | null>(null)
  const [integrationModule, setIntegrationModule] = useState<IntegrationModule | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  async function fetch() {
    if (!installationId) return

    try {
      setLoading(true)
      const response = await getInstallation(installationId)
      if (response.response) {
        setInstallation(response.response.data)
        const integrationModuleId = response.response.data.integration_module
        const integrationResponse = await getIntegrationModule(integrationModuleId)
        if (integrationResponse.response) {
          setIntegrationModule(integrationResponse.response.data)
        }
      }
      setLoading(false)
    } catch (error) {
      console.error('Error fetching installation:', error)
      toast.error('Error fetching installation details.')
      setLoading(false)
      return
    }
  }

  useEffect(() => {
    fetch()
  }, [installationId])

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          width: '65%',
          padding: 10,
          overflowY: 'auto',
          boxShadow: '0px 0px 24px 8px rgba(0,0,0,0.25)', // Strong shadow
        },
      }}
    >
      {loading && <Loading />}
      {!loading && !installation && <div>No installation found.</div>}
      {!loading && installation && integrationModule && (
        <Stack spacing={2} padding={1}>
          <Grid container direction={'row'} justifyContent="space-between" alignItems="start">
            <Grid item>
              <Box flexDirection={'row'} display="flex" alignItems="center">
                <Avatar
                  sizes="large"
                  sx={{
                    width: 60,
                    height: 60,
                    backgroundColor: 'secondary.main',
                  }}
                >
                  {mapping['Installation']}
                </Avatar>
                <Stack spacing={1} pl={2}>
                  <Typography variant="h1" fontWeight={'bold'}>
                    {installation?.name}
                  </Typography>
                  <Text variant="caption-bold" color="color-neutral">
                    {installation?.id}
                  </Text>
                </Stack>
              </Box>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center">
                <InstallationEditAction
                  installation={installation}
                  integrationModule={integrationModule}
                  onInstallationUpdated={setInstallation}
                />
                <Tooltip title="Open full screen">
                  <IconButton
                    onClick={e => {
                      window.open(`${ROUTES.INSTALLATIONS}/${installation.id}`, '_blank')
                      e.preventDefault()
                    }}
                    scale="default"
                  >
                    <OpenInNew />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          <InstallationDetailContainer
            installation={installation}
            integrationModule={integrationModule}
          />
        </Stack>
      )}
    </Drawer>
  )
}

export default InstallationQuickView
